import { useState, type ReactNode } from "react";
import {
  hasAnyUserRole,
  hasMePermission,
  hasOrPermission,
  type MePermissions,
  type UserRole,
} from "@/app";
import { GnbButton } from "@/components/buttons";
import { BarChartIcon } from "@/components/icons";
import { HatTextTooltip } from "@/components/tooltip";
import { HatcheryLogo } from "./HatcheryLogo";
import styles from "./Gnb.module.css";
import {
  Text,
  CheckCircleIcon,
  ChevronIcon,
  BeakerIcon,
  ClockIcon,
  FolderIcon,
  GaugeIcon,
  GpuCardIcon,
  GraphLineIcon,
  ServerIcon,
  TagIcon,
  ToolsIcon,
  UserAddOutlineIcon,
} from "@carbon6/c6-frontend-design";
import { hatcheryTextModes } from "@/app/theme";

export enum GnbMenu {
  Debug = "debug",
  // 조직 데이터셋 횡단 통계다. 단건 상세의 진단 탭과 달리 전체 데이터셋을 한 번에 본다.
  // 그래서 Datasets 안이 아니라 그 앞의 독립 항목이다.
  DataDashboard = "dataDashboard",
  // 학습은 학습 세션 목록이다. 내 것/팀 것은 페이지 안 스코프 스위처로 구분한다.
  Training = "training",
  Dataset = "dataset",
  // 통합 리뷰는 데이터셋과 라벨 리뷰를 한 메뉴에서 다룬다.
  Review = "review",
  Label = "label",
  Embodiment = "embodiment",
  TrainingEvaluation = "trainingEvaluation",
  // 예약 실행(스케줄) 운영이다. 공용 workspace 의 반복 학습 제출을 보고 관리한다. 개인 학습
  // 목록(Training)과 스코프가 다르다. 그래서 Workspace 가 아니라 Operations 에 둔다.
  TrainingSchedule = "trainingSchedule",
  // Workspace GPU 한도·점유·사용량이다. 조직 전체 스코프이므로 Training 의 My/Team 축과 섞지 않는다.
  GpuQuota = "gpuQuota",
  AdminMembers = "adminMembers",
  AdminWorkers = "adminWorkers",
  // 워커가 남긴 스토리지 잔여를 운영자가 회수하는 화면이다. UI 명칭은 Storage 다. 이 이름으로
  // Datasets 의 보관됨(archived) 탭과 구분한다.
  AdminStorage = "adminStorage",
  // 수집기가 5분마다 기록한 DB·API pod 스냅샷이다. 워커 하트비트(Workers)와 축이 다르다.
  // 요청 로그·MCP tool 이벤트는 여기 없다. 그 데이터는 Cloud Logging 이 소유한다.
  AdminObservability = "adminObservability",
}

type GnbMenuItem = {
  menu: GnbMenu;
  label: string;
  icon: ReactNode;
  to?: string;
  disabled?: boolean;
  // 항목 단위 권한 게이트다. 섹션은 공개이고 이 항목만 특정 role 에게 노출할 때 쓴다
  // (예: 공개 데이터셋 상단 그룹 안의 admin 전용 아카이브/리뷰).
  requiredRoles?: readonly UserRole[];
  // 서버가 계산한 permission 게이트다. 플래그가 없으면 항목을 완전히 숨긴다. role 과 달리
  // 그룹 소속 같은 서버 정책이 정하는 축이다. Gnb 는 선언적 키만 안다.
  requiredPermission?: keyof MePermissions;
  // Role 게이트를 **넓히는** permission 이다. requiredRoles 를 만족하지 않아도 이 플래그가
  // true 면 항목을 표시한다(Schedule: admin 등급 또는 research 소속). requiredPermission 은
  // AND 축이지만 이 값은 OR 축이다. Permissions 를 받지 못하면(null) 이 축만 닫고 role 축은 유지한다.
  orPermission?: keyof MePermissions;
};

type GnbSection = {
  // 접힘 상태 저장과 React key 에 쓰는 식별자다.
  key: string;
  // 섹션 헤더 라벨이다. 없으면 primary 다(헤더 없이 항상 노출되는 상단 그룹).
  title?: string;
  // 섹션 전체를 볼 수 있는 권한이다. 없으면 모두에게 노출한다.
  requiredRoles?: readonly UserRole[];
  items: readonly GnbMenuItem[];
};

const gnbSections: readonly GnbSection[] = [
  {
    // Workspace 는 데이터셋·학습·평가 계열이다. 운영/관리처럼 접이식 섹션이다.
    key: "training",
    title: "Workspace",
    items: [
      // 사용자가 목록보다 전체 통계를 먼저 보도록 Datasets 앞에 둔다.
      {
        menu: GnbMenu.DataDashboard,
        label: "Data Dashboard",
        icon: <BarChartIcon />,
        to: "/data-dashboard",
      },
      {
        menu: GnbMenu.Dataset,
        label: "Datasets",
        icon: <FolderIcon />,
        to: "/datasets",
      },
      {
        menu: GnbMenu.Training,
        label: "Training",
        icon: <BeakerIcon />,
        to: "/training",
      },
      {
        menu: GnbMenu.TrainingEvaluation,
        label: "Evaluation",
        icon: <GraphLineIcon />,
        to: "/training/evaluation",
        requiredPermission: "can_use_evaluation",
      },
      {
        menu: GnbMenu.GpuQuota,
        label: "GPU Quota",
        icon: <GpuCardIcon />,
        to: "/training/gpu-quota",
      },
    ],
  },
  {
    // 운영 섹션이다: 카탈로그·라벨 구성, 리뷰, 스케줄. 게이트는 섹션이 아니라 항목이 가진다.
    // Schedule 은 research 멤버에게도 열린다. 그래서 섹션 게이트로는 "운영자만" 을 표현할 수 없다.
    // 노출 항목이 없으면 섹션 자체가 사라진다. 그래서 member 의 평시 화면은 바뀌지 않는다.
    // hasAnyUserRole 은 등급 순서가 아니라 정확히 일치하는 role 만 본다. 그래서 admin 도 함께 적는다.
    key: "operations",
    title: "Operations",
    items: [
      {
        menu: GnbMenu.Embodiment,
        label: "Embodiments",
        icon: <ToolsIcon />,
        to: "/embodiments",
        requiredRoles: ["admin"],
      },
      {
        menu: GnbMenu.Label,
        label: "Labels",
        icon: <TagIcon />,
        to: "/labels",
        requiredRoles: ["admin"],
      },
      {
        menu: GnbMenu.Review,
        label: "Reviews",
        icon: <CheckCircleIcon />,
        to: "/review",
        requiredRoles: ["admin"],
      },
      {
        menu: GnbMenu.TrainingSchedule,
        label: "Schedule",
        icon: <ClockIcon />,
        to: "/training/schedules",
        requiredRoles: ["admin"],
        orPermission: "can_use_schedule_operations",
      },
    ],
  },
  {
    // 관리 섹션이다. 멤버·워커·스토리지를 운영한다. admin 전용 섹션이다.
    key: "admin",
    title: "Administration",
    requiredRoles: ["admin"],
    items: [
      {
        menu: GnbMenu.AdminMembers,
        label: "Members",
        icon: <UserAddOutlineIcon />,
        to: "/admin/members",
      },
      {
        menu: GnbMenu.AdminWorkers,
        label: "Workers",
        icon: <ServerIcon />,
        to: "/admin/workers",
      },
      {
        menu: GnbMenu.AdminStorage,
        label: "Storage",
        icon: <GraphLineIcon />,
        to: "/admin/storage",
      },
      {
        menu: GnbMenu.AdminObservability,
        label: "Observability",
        icon: <GaugeIcon />,
        to: "/admin/observability",
      },
    ],
  },
];

const collapsedSectionsStorageKey = "hatchery-registry:gnb-collapsed-sections";

type GnbProps = {
  currentMenu: GnbMenu;
  appName?: string;
  collapsed?: boolean;
  // 브랜드 타이틀 옆 슬롯이다(예: 계정 메뉴 chevron). 데이터와 핸들러는 호출자(PageBase)가 소유한다.
  brandMenu?: ReactNode;
  // 배너와 메뉴 사이 숏컷 공간(위아래 구분선)이다. 데이터셋 업로드·새 학습 같은 전역 진입점을 둔다.
  // 버튼과 핸들러는 호출자(PageBase)가 소유한다. 없으면 구분선을 포함한 공간 전체를 그리지 않는다.
  shortcuts?: ReactNode;
  // 메뉴 항목 라벨 오른쪽 슬롯(메뉴 → 노드)이다. 예: Evaluation 의 진행 중 실행 개수 배지.
  // 데이터와 폴링은 호출자(PageBase)가 소유한다. 그래서 전역 메뉴는 도메인을 모른다.
  menuAccessories?: Partial<Record<GnbMenu, ReactNode>>;
  // 브랜드 행 우측에 둘 요소다(예: collapse 토글). 버튼은 호출자(PageBase)가 소유한다.
  collapseControl?: ReactNode;
  // 브랜드 바로 아래의 서버 컨텍스트 슬롯이다. 현재 환경·버전 같은 배포 식별자를 둔다. 이 값은
  // 모든 화면에서 먼저 보여야 하지만 메뉴나 경고 배너에는 속하지 않는다. 데이터는 호출자가 소유한다.
  serverContext?: ReactNode;
  // 브랜드 행과 메뉴 사이 슬롯이다. 화면과 관계없이 항상 표시할 알림을 둔다(예: 새 버전 안내).
  // 데이터와 핸들러는 호출자(PageBase)가 소유한다.
  banner?: ReactNode;
  // 현재 사용자 등급이다(로그인 전이면 null). admin 전용 섹션·항목을 비-admin에게 숨길 때 쓴다.
  currentUserRole?: UserRole | null;
  // 서버가 계산한 permission(/v1/me)이다. requiredPermission 항목을 거를 때 쓴다. 값을 받지
  // 못하면(null) fail closed 로 숨긴다.
  currentUserPermissions?: MePermissions | null;
};

export function Gnb({
  appName = "Hatchery",
  brandMenu,
  shortcuts,
  menuAccessories,
  collapseControl,
  serverContext,
  banner,
  collapsed = false,
  currentMenu,
  currentUserRole = null,
  currentUserPermissions = null,
}: GnbProps) {
  // 접힌 섹션 key 집합이다. localStorage 에 상태를 저장한다(B: 접기 + 상태 기억).
  const [collapsedSections, setCollapsedSections] = useState<ReadonlySet<string>>(() =>
    readCollapsedSections(),
  );

  // 섹션 접근권과 항목 단위 권한(role·permission)을 함께 적용한다. 노출 항목이 없는 섹션은
  // 제거한다. orPermission 은 role 축을 넓히고, requiredPermission 은 AND 로 적용된다.
  const accessibleSections = gnbSections
    .filter((section) => hasAnyUserRole(currentUserRole, section.requiredRoles))
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          (hasAnyUserRole(currentUserRole, item.requiredRoles) ||
            hasOrPermission(currentUserPermissions, item.orPermission)) &&
          hasMePermission(currentUserPermissions, item.requiredPermission),
      ),
    }))
    .filter((section) => section.items.length > 0);

  function toggleSection(key: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      writeCollapsedSections(next);
      return next;
    });
  }

  return (
    <div className={styles.shell} data-collapsed={collapsed ? "true" : "false"}>
      <div className={styles.brand}>
        {/* 접힘 레일에서는 로고가 맨 아래 계정 트리거로 옮겨진다. 그래서 브랜드 행에서는 숨긴다. */}
        {collapsed ? null : (
          <>
            <HatcheryLogo className={styles.hatcheryLogo} />
            <Text variant={hatcheryTextModes["title-bold"]} className={styles.brandTitle}>
              {appName}
            </Text>
            {brandMenu ? <div className={styles.brandMenu}>{brandMenu}</div> : null}
          </>
        )}
        {collapseControl ? <div className={styles.brandAccessory}>{collapseControl}</div> : null}
      </div>

      {serverContext ? <div className={styles.serverContext}>{serverContext}</div> : null}

      {banner}

      {shortcuts ? <div className={styles.shortcuts}>{shortcuts}</div> : null}

      <nav className={styles.nav} aria-label="전역 메뉴">
        {accessibleSections.map((section) => {
          // Primary(헤더 없음) 또는 접힘 레일이면 헤더 없이 항목만 아이콘으로 나열한다.
          if (section.title == null || collapsed) {
            return (
              <div key={section.key} className={styles.section}>
                {section.items.map((item) => (
                  <NavItem
                    key={item.menu}
                    item={item}
                    currentMenu={currentMenu}
                    collapsed={collapsed}
                    accessory={menuAccessories?.[item.menu]}
                  />
                ))}
              </div>
            );
          }

          const sectionCollapsed = collapsedSections.has(section.key);
          return (
            <div key={section.key} className={styles.section}>
              <button
                type="button"
                className={styles.sectionHeader}
                aria-expanded={!sectionCollapsed}
                onClick={() => toggleSection(section.key)}
              >
                <span className={styles.sectionTitle}>{section.title}</span>
                <ChevronIcon
                  className={styles.sectionChevron}
                  data-collapsed={sectionCollapsed ? "true" : "false"}
                />
              </button>
              {sectionCollapsed
                ? null
                : section.items.map((item) => (
                    <NavItem
                      key={item.menu}
                      item={item}
                      currentMenu={currentMenu}
                      collapsed={collapsed}
                      accessory={menuAccessories?.[item.menu]}
                    />
                  ))}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

function NavItem({
  item,
  currentMenu,
  collapsed,
  accessory,
}: {
  item: GnbMenuItem;
  currentMenu: GnbMenu;
  collapsed: boolean;
  accessory?: ReactNode;
}) {
  return (
    <SidebarMenuTooltip label={item.label} disabled={!collapsed}>
      <GnbButton
        className={styles.navItem}
        to={item.to}
        disabled={item.disabled}
        current={item.menu === currentMenu}
        aria-label={collapsed ? item.label : undefined}
      >
        <span className={styles.navIcon} aria-hidden="true">
          {item.icon}
        </span>
        <span className={styles.fullLabel}>{item.label}</span>
        {accessory ? <span className={styles.navAccessory}>{accessory}</span> : null}
      </GnbButton>
    </SidebarMenuTooltip>
  );
}

function SidebarMenuTooltip({
  children,
  disabled,
  label,
}: {
  children: ReactNode;
  disabled: boolean;
  label: string;
}) {
  return (
    <HatTextTooltip text={label} placement="right" disabled={disabled} mode="plain">
      {children}
    </HatTextTooltip>
  );
}

function readCollapsedSections(): ReadonlySet<string> {
  if (typeof window === "undefined") {
    return new Set();
  }
  try {
    const raw = window.localStorage.getItem(collapsedSectionsStorageKey);
    if (!raw) {
      return new Set();
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((key): key is string => typeof key === "string"))
      : new Set();
  } catch {
    return new Set();
  }
}

function writeCollapsedSections(keys: ReadonlySet<string>) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(collapsedSectionsStorageKey, JSON.stringify([...keys]));
  } catch {
    // 사설 모드나 제한된 브라우저 환경에서는 Storage 를 쓸 수 없을 수 있다.
  }
}
